import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import AddchartRoundedIcon from '@mui/icons-material/AddchartRounded';
import { chatTokens } from '../theme/chatTokens';

interface MetricCardProps {
  metricName: string;
  metricValue: any;
  workflowId?: string;
  eventId?: string;
  onAddToDashboard?: (workflowId: string, eventId: string, title: string, widgetType?: string) => void;
  compact?: boolean;  // For multi-metric display
  fillContainer?: boolean;  // Fill 100% width/height (for dashboard widgets)
  hideLabel?: boolean;  // Hide metric name label (when shown in widget title)
  transparentBg?: boolean;  // Use transparent background instead of colored
}

function formatMetricValue(value: any): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Coerce numeric strings (e.g. "46950.00" from some backends) to numbers
  if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) {
    value = Number(value);
  }

  if (typeof value === 'number') {
    // Format numbers with commas and appropriate decimal places
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }

    // For large numbers, use at most 2 decimal places
    if (Math.abs(value) >= 1000) {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }

    // For smaller decimals, show 2 decimal places
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Easing function for smooth animation
function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

// Custom hook for counting animation
function useCountUp(end: number, duration: number = 1500): number {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Reset if end value changes
    setCount(0);
    startTimeRef.current = undefined;

    const animate = (currentTime: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);
      
      // Round to whole number for integer display
      setCount(Math.round(easedProgress * end));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end, duration]);

  return count;
}

export function MetricCard({ 
  metricName, 
  metricValue, 
  workflowId, 
  eventId, 
  onAddToDashboard, 
  compact,
  fillContainer = false,
  hideLabel = false,
  transparentBg = false
}: MetricCardProps) {
  const displayName = toTitleCase(metricName);
  const theme = useTheme();
  const t = theme.palette.mode === 'dark' ? chatTokens.dark : chatTokens.light;

  const showAddToDashboard = !compact && workflowId && eventId && onAddToDashboard;
  
  // Determine if we should animate (only for numbers)
  const shouldAnimate = typeof metricValue === 'number';
  const animatedValue = useCountUp(shouldAnimate ? metricValue : 0, 1500);
  
  // Format the value (use animated value for numbers)
  const valueToFormat = shouldAnimate ? animatedValue : metricValue;
  const formattedValue = formatMetricValue(valueToFormat);

  // Fixed font sizes for consistent display
  const fontSize = compact ? '2rem' : '3.5rem';

  // Adaptive font size: shorter values get a much larger size so the number
  // fills the card and reads as an impactful KPI, not a caption.
  const adaptiveFontSize = (val: string): string => {
    const len = val.replace(/\s/g, '').length;
    if (len <= 3)  return 'clamp(3rem, 6vw, 5rem)';
    if (len <= 5)  return 'clamp(2.5rem, 5vw, 4rem)';
    if (len <= 8)  return 'clamp(2rem, 4vw, 3.2rem)';
    if (len <= 11) return 'clamp(1.6rem, 3vw, 2.5rem)';
    return 'clamp(1.2rem, 2.5vw, 1.9rem)';
  };

  // ── Dashboard widget (fillContainer) ── polished layout matching WorkspacePanel
  if (fillContainer) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          gap: 0.5,
          px: 2,
          py: 1.5,
        }}
      >
        {/* Metric name label — only shown when hideLabel is false */}
        {!hideLabel && (
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: 'text.secondary',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {displayName}
          </Typography>
        )}

        {/* Animated value */}
        <Typography
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: adaptiveFontSize(formattedValue),
            lineHeight: 1.05,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'center',
            wordBreak: 'break-word',
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    );
  }

  // ── Inline / compact (chat message) ── original layout untouched
  return (
    <Paper 
      elevation={0}
      sx={{ 
        m: 0,
        p: compact ? 2 : 3,
        bgcolor: transparentBg ? 'transparent' : t.assistantBg,
        border: 'none',
        borderRadius: '12px',
        boxShadow: t.messageShadow,
        width: 'auto',
        height: 'auto',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        transition: 'all 0.2s ease',
        minWidth: compact ? '200px' : '280px',
        maxWidth: compact ? '350px' : '500px',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Add to Dashboard Icon - Top Right */}
      {showAddToDashboard && (
        <Tooltip title="Add to Dashboard" placement="top">
          <IconButton
            size="small"
            onClick={() => onAddToDashboard(workflowId, eventId, displayName, 'metric_card')}
            sx={{ 
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'primary.50',
              },
            }}
          >
            <AddchartRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      
      {/* Metric Name */}
      {!hideLabel && (
        <Box sx={{ width: '100%', position: 'relative' }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            noWrap
            sx={{ 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: compact ? '0.7rem' : '0.75rem',
              mb: compact ? 1 : 2,
              width: '100%',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              boxSizing: 'border-box',
              px: showAddToDashboard ? 5 : 0,
            }}
          >
            {displayName}
          </Typography>
        </Box>
      )}
      
      {/* Metric Value */}
      <Typography 
        variant={compact ? "h4" : "h2"} 
        sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          lineHeight: 1.2,
          fontSize: fontSize,
          wordBreak: 'break-word',
          textAlign: 'center',
        }}
      >
        {formattedValue}
      </Typography>
    </Paper>
  );
}
